import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  getDoc,
  limit,
} from "firebase/firestore";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  CircularProgress,
  Link,
  Alert,
  Avatar,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { alpha } from "@mui/material/styles";
import { getSession } from "../session";
import Header3 from "./Header3";
import ChatPolicyDialog from "./ChatPolicyDialog";
import { detectContactInfoAttempt, applyModerationStrike } from "./moderation";

function ChatBox() {
  const { projectId, proposalId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [authUser, setAuthUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [moderationAlert, setModerationAlert] = useState(null);
  const [senderPhotos, setSenderPhotos] = useState({});

  const fetchAuthUserAndMessages = useCallback(async () => {
    try {
      const user = await getSession();
      if (!user) {
        throw new Error("No authenticated user found.");
      }

      const userDocRef = doc(db, "users", user.uid);
      const userDocSnapshot = await getDoc(userDocRef);

      if (userDocSnapshot.exists()) {
        const userData = userDocSnapshot.data();
        setAuthUser({
          uid: user.uid,
          displayName: userData.displayName || user.email,
          email: user.email,
        });

        if (!projectId || !proposalId) {
          throw new Error("Project ID or Proposal ID is missing");
        }

        const messagesPath = `projects/${projectId}/proposals/${proposalId}/messages`;
        const messagesRef = collection(db, messagesPath);
        const q = query(messagesRef, orderBy("timestamp", "desc"), limit(50));

        const unsubscribe = onSnapshot(q, (snapshot) => {
          const newMessages = snapshot.docs
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
            .reverse();
          setMessages(newMessages);
          scrollToBottom();
        });

        return () => unsubscribe();
      } else {
        throw new Error("User document not found in Firestore");
      }
    } catch (error) {
      console.error("Error in fetchAuthUserAndMessages:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, proposalId]);

  useEffect(() => {
    fetchAuthUserAndMessages();
  }, [fetchAuthUserAndMessages]);

  useEffect(() => {
    const missingIds = [...new Set(messages.map((m) => m.senderId))].filter(
      (uid) => uid && !(uid in senderPhotos)
    );
    if (missingIds.length === 0) return;

    (async () => {
      const entries = await Promise.all(
        missingIds.map(async (uid) => {
          const userSnap = await getDoc(doc(db, "users", uid));
          return [uid, userSnap.exists() ? userSnap.data().profileImageUrl || null : null];
        })
      );
      setSenderPhotos((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (newMessage.trim() === "" || !authUser || !projectId || !proposalId) {
      return;
    }

    const messagesPathForCheck = `projects/${projectId}/proposals/${proposalId}/messages`;
    const { flagged, reasons } = detectContactInfoAttempt(newMessage);

    if (flagged) {
      try {
        const { accountStatus } = await applyModerationStrike({
          uid: authUser.uid,
          chatPath: messagesPathForCheck,
          message: newMessage.trim(),
          reasons,
        });
        setModerationAlert(
          accountStatus === "pending_deletion"
            ? "Volviste a intentar compartir información de contacto. Tu cuenta fue marcada para revisión y puede ser eliminada."
            : "Tu mensaje parece incluir información de contacto. Está prohibido coordinar trabajo fuera de la plataforma. Tu cuenta quedó suspendida."
        );
        setTimeout(() => navigate("/home"), 3000);
      } catch (moderationError) {
        console.error("Error applying moderation strike:", moderationError);
        setModerationAlert(
          "Tu mensaje parece incluir información de contacto y no fue enviado. Compartir estos datos no está permitido."
        );
      }
      return;
    }

    const message = {
      content: newMessage.trim(),
      timestamp: new Date(),
      senderId: authUser.uid,
      senderDisplayName: authUser.displayName,
      read: false,
    };

    const messagesPath = `projects/${projectId}/proposals/${proposalId}/messages`;

    try {
      const messagesRef = collection(db, messagesPath);
      await addDoc(messagesRef, message);
      setNewMessage("");
      scrollToBottom();
    } catch (error) {
      console.error("Error sending message: ", error);
      setError("Failed to send message. Please try again.");
    }
  };

  const linkify = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, index) =>
      urlRegex.test(part) ? (
        <Link key={index} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </Link>
      ) : (
        part
      )
    );
  };

  const formatTime = (timestamp) => {
    const date = timestamp?.toDate ? timestamp.toDate() : timestamp;
    if (!date) return "";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const markMessageAsRead = useCallback(
    async (messageId) => {
      if (!projectId || !proposalId) return;

      const messageRef = doc(
        db,
        `projects/${projectId}/proposals/${proposalId}/messages`,
        messageId
      );
      try {
        await updateDoc(messageRef, { read: true });
      } catch (error) {
        console.error("Error marking message as read: ", error);
      }
    },
    [projectId, proposalId]
  );

  useEffect(() => {
    if (authUser) {
      messages.forEach((message) => {
        if (message.senderId !== authUser.uid && !message.read) {
          markMessageAsRead(message.id);
        }
      });
    }
  }, [messages, authUser, markMessageAsRead]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "background.default",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ChatPolicyDialog
        open={!policyAccepted}
        onAccept={() => setPolicyAccepted(true)}
        onCancel={() => navigate(-1)}
      />
      <Header3 />
      {moderationAlert && (
        <Alert
          severity="error"
          icon={<WarningAmberIcon />}
          sx={{ mx: "auto", mt: 10, maxWidth: 980, width: "100%" }}
        >
          {moderationAlert}
        </Alert>
      )}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 980,
            mx: "auto",
            px: 2,
            pt: 10,
            pb: 2,
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {messages.length === 0 ? (
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                gap: 1,
              }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 48 }} />
              <Typography variant="body1">Todavía no hay mensajes</Typography>
              <Typography variant="body2">Escribí el primero para arrancar la conversación</Typography>
            </Box>
          ) : (
            messages.map((message) => {
              const isOwn = message.senderId === authUser.uid;
              return (
                <Box
                  key={message.id}
                  sx={{
                    display: "flex",
                    flexDirection: isOwn ? "row-reverse" : "row",
                    alignSelf: isOwn ? "flex-end" : "flex-start",
                    alignItems: "flex-end",
                    gap: 1,
                    maxWidth: "75%",
                    mb: 2,
                  }}
                >
                  <Avatar
                    src={senderPhotos[message.senderId]}
                    sx={{ width: 28, height: 28, fontSize: 14, bgcolor: "secondary.main" }}
                  >
                    {message.senderDisplayName?.[0]?.toUpperCase() || "?"}
                  </Avatar>
                  <Box>
                    <Box
                      sx={{
                        bgcolor: isOwn
                          ? (theme) => alpha(theme.palette.primary.main, 0.18)
                          : "background.paper",
                        border: (theme) =>
                          isOwn ? "none" : `1px solid ${theme.palette.divider}`,
                        p: 1.5,
                        borderRadius: "16px",
                        borderBottomRightRadius: isOwn ? "4px" : "16px",
                        borderBottomLeftRadius: isOwn ? "16px" : "4px",
                        boxShadow: isOwn ? "none" : 1,
                      }}
                    >
                      {!isOwn && (
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", color: "secondary.main", mb: 0.5 }}
                        >
                          {message.senderDisplayName}
                        </Typography>
                      )}
                      <Typography variant="body1" sx={{ color: "text.primary", whiteSpace: "pre-wrap" }}>
                        {linkify(message.content)}
                      </Typography>
                    </Box>
                    {detectContactInfoAttempt(message.content).flagged && (
                      <Typography
                        variant="caption"
                        sx={{ color: "warning.main", display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
                      >
                        <WarningAmberIcon sx={{ fontSize: 14 }} />
                        Este mensaje podría contener información de contacto
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        mt: 0.5,
                        display: "block",
                        textAlign: isOwn ? "right" : "left",
                      }}
                    >
                      {formatTime(message.timestamp)}
                      {isOwn && ` · ${message.read ? "Leído" : "Enviado"}`}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Box>
      </Box>
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ maxWidth: 980, mx: "auto", p: 2 }}>
          <TextField
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Escribe tu mensaje..."
            variant="outlined"
            fullWidth
            multiline
            maxRows={4}
            disabled={!policyAccepted}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "24px" } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || !policyAccepted}
                    sx={{ minWidth: 0, borderRadius: "50%", width: 40, height: 40, p: 0 }}
                  >
                    <SendIcon fontSize="small" />
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default ChatBox;
